import { useRef } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowRight, Building2, Loader2, Phone, Square, User } from "lucide-react";
import {
  APPOINTMENT_STATUS_COLORS,
  APPOINTMENT_STATUS_LABELS,
} from "@bookdental/schemas";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { AJUDA_AGENDA } from "@/lib/ajuda/dialogos/agenda";
import { buildWhatsAppUrl, WhatsAppIcon } from "@/lib/whatsapp";
import { formatPhone } from "@/components/patients/PatientListRow";
import type { WeekAppointment } from "@/lib/dashboard-week";
import { useAcoesDaConsulta } from "./use-acoes-da-consulta";
import { duracaoAgendadaPorExtenso, tempoDeAtendimento } from "./appt-card-info";
import { RemarcarOuCancelar } from "./RemarcarOuCancelar";
import { StatusDaConsulta } from "@/components/agenda/consulta/StatusDaConsulta";

/**
 * Resumo operacional da consulta clicada no painel — não um formulário.
 *
 * A versão anterior era uma grade de seis cartões rótulo/valor com moldura cada, o que fragmentava
 * a leitura e dava peso igual a "Horário" e a "Agendado em". Aqui a hierarquia é explícita: um
 * bloco destacado com quando/o quê, contato como ação, UMA ação principal que muda com o status, e
 * a auditoria no rodapé, discreta.
 */
export function AppointmentDetailDialog({
  appointment,
  onClose,
  esconderLinkDoPaciente = false,
}: {
  appointment: WeekAppointment | null;
  onClose: () => void;
  /** Na ficha do paciente o link seria redundante — já se está nela. */
  esconderLinkDoPaciente?: boolean;
}) {
  const conteudoRef = useRef<HTMLDivElement>(null);
  const a = useAcoesDaConsulta(appointment, onClose);
  const { acao, pendente } = a;
  const status = a.status ?? appointment?.status;

  // O telefone do paciente é mais atual que o copiado na consulta, então vem primeiro.
  const telefone = appointment?.patient?.phone ?? appointment?.patientPhone ?? null;
  const nomeDoPaciente = appointment?.patient?.name ?? appointment?.patientName ?? "";

  const dia = appointment
    ? (() => {
        const l = format(new Date(appointment.date), "EEEE, d 'de' MMMM", { locale: ptBR });
        return l.charAt(0).toUpperCase() + l.slice(1);
      })()
    : "";
  const duracao = appointment ? duracaoAgendadaPorExtenso(appointment) : "";
  const real = appointment ? tempoDeAtendimento(appointment) : "";

  return (
    <Dialog open={!!appointment} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        ref={conteudoRef}
        closeLabel="Fechar detalhes da consulta"
        // Sem isso o Radix foca o primeiro botão ao abrir e o anel de foco fica aceso, o que lê
        // como estado travado. Focando o próprio contêiner o teclado continua preso no diálogo.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          conteudoRef.current?.focus();
        }}
        className="max-w-md dark:bg-card"
      >
        {appointment && (
          <>
            <DialogHeader className="pr-10">
              <div className="flex flex-wrap items-center gap-2">
                <DialogTitle ajuda={AJUDA_AGENDA.detalhesDaConsulta}>{nomeDoPaciente}</DialogTitle>
                {a.podeMudarStatus ? (
                  <StatusDaConsulta value={status!} onChange={a.mudarStatus} />
                ) : (
                <span
                  className="rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide"
                  style={{
                    backgroundColor: `${APPOINTMENT_STATUS_COLORS[status!]}1f`,
                    color: APPOINTMENT_STATUS_COLORS[status!],
                  }}
                >
                  {APPOINTMENT_STATUS_LABELS[status!]}
                </span>
                )}
              </div>
              <DialogDescription>Detalhes da consulta</DialogDescription>
            </DialogHeader>

            {/* Consulta de outra clínica (aberta pela Pessoal ou pelo painel): fica claro onde se age. */}
            {a.clinicaDeFora && (
              <p className="-mt-1 flex items-start gap-2 rounded-lg border border-brand-100 bg-brand-50 px-3 py-2 text-[12.5px] text-brand-800 dark:border-primary/20 dark:bg-primary/10 dark:text-primary">
                <Building2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Consulta da <b>{a.clinicaDeFora}</b> · você age nela sem trocar a organização.
                  {a.somenteLeitura && " Só o profissional da consulta pode iniciar o atendimento."}
                </span>
              </p>
            )}

            {/* Bloco único, sem moldura por dado: quando e o quê lidos de uma vez. */}
            <div className="rounded-xl border border-brand-100 bg-brand-50/60 p-4 dark:border-primary/20 dark:bg-primary/10">
              <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">{dia}</p>
              <p className="mt-0.5 text-lg font-bold tabular-nums text-zinc-900 dark:text-foreground">
                {appointment.startTime} – {appointment.endTime}
                {duracao && (
                  <span className="ml-2 text-sm font-medium text-zinc-500 dark:text-muted-foreground">
                    · {duracao}
                  </span>
                )}
              </p>
              {real && (
                <p className="mt-0.5 text-xs font-semibold text-brand-700 dark:text-primary">{real}</p>
              )}

              {(appointment.procedure || appointment.dentistName || appointment.sala || appointment.notes) && (
                <div className="mt-3 space-y-1 border-t border-brand-100 pt-3 dark:border-primary/20">
                  {appointment.procedure && (
                    <p className="text-sm font-medium text-zinc-900 dark:text-foreground">
                      {appointment.procedure}
                    </p>
                  )}
                  {(appointment.dentistName || appointment.sala) && (
                    <p className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-muted-foreground">
                      {appointment.dentistName}
                      {appointment.sala && (
                        <>
                          {appointment.dentistName && <span className="opacity-60">·</span>}
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: appointment.sala.cor ?? "#a1a1aa" }} />
                          {appointment.sala.nome}
                        </>
                      )}
                    </p>
                  )}
                  {appointment.notes && (
                    <p className="whitespace-pre-wrap pt-1 text-xs italic text-zinc-600 dark:text-muted-foreground">
                      {appointment.notes}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Contato: o número e o que fazer com ele, juntos — antes o telefone era um cartão e as
                ações vinham repetidas embaixo. */}
            {telefone && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
                  Contato
                </p>
                <p className="mt-0.5 text-sm font-medium tabular-nums text-zinc-900 dark:text-foreground">
                  {formatPhone(telefone)}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <a
                    href={buildWhatsAppUrl(telefone, nomeDoPaciente)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-emerald-200 px-3 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                    WhatsApp
                  </a>
                  <a
                    href={`tel:${telefone.replace(/\D/g, "")}`}
                    className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-zinc-200 px-3 text-sm font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
                  >
                    <Phone className="h-4 w-4" />
                    Ligar
                  </a>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {acao && a.acaoDisponivel && (
                <button
                  type="button"
                  disabled={pendente}
                  onClick={a.executarAcao}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  {pendente ? <Loader2 className="h-4 w-4 animate-spin" /> : <acao.icone className="h-4 w-4" />}
                  {acao.rotulo}
                </button>
              )}

              {/* Encerrar não é a ação principal de quem está em atendimento (essa é voltar pra
                  cadeira), mas some daqui seria perder o único jeito de fechar sem abrir a ficha. */}
              {a.podeEncerrar && (
                <button
                  type="button"
                  disabled={pendente}
                  onClick={a.encerrar}
                  className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-emerald-200 px-4 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-60 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10"
                >
                  <Square className="h-4 w-4" />
                  Encerrar atendimento
                </button>
              )}

              <RemarcarOuCancelar appointment={appointment} onConcluido={onClose} />

              {/* Terciário: link, não mais um botão — competir com a ação principal era o que
                  deixava o diálogo com cara de formulário. */}
              {appointment.patientId && !esconderLinkDoPaciente && (
                <button
                  type="button"
                  onClick={a.verPaciente}
                  className="inline-flex h-9 items-center justify-center gap-1.5 self-center rounded-md px-2 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-primary dark:hover:bg-primary/10"
                >
                  <User className="h-4 w-4" />
                  Ver perfil do paciente
                  <ArrowRight className="h-4 w-4" />
                </button>
              )}
            </div>

            {appointment.createdAt && (
              <p className="-mt-1 text-center text-[11px] text-zinc-400 dark:text-muted-foreground">
                Agendamento criado em{" "}
                {format(new Date(appointment.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
              </p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
